import React, { useCallback, useContext, useMemo } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import PhoneInput from 'react-phone-number-input/input';
import { visuallyHidden } from '@mui/utils';
import { AppContext } from '../../../context/app-context';
import { UserRoleToTypeName, UserType } from '../../../components/constants/securspace-constants';
import { PhoneNumberTextField } from '../../../components/form/PhoneNumberTextField';
import { formatPhoneNumber } from '../../../util/ValidationUtils';
import { useLocationManagers } from '../../../hooks/location/useLocationManagers';
import { LocationManagerData, LocationWizardData, NewLocationManagerData } from '../types';
import {
  ADD_NEW_MANAGER_VALUE,
  createEmptyNewLocationManager,
  getNewLocationManagerErrors,
  getNewLocationManagerRole,
} from '../newLocationManager';

interface LocationManagerStepProps {
  data: LocationWizardData;
  updateData: (
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => void;
}

const PHONE_FORMAT_HINT = 'Enter a valid 10-digit phone number';
const PHONE_VALID_HINT = '\u2713 Valid phone number';
const EMAIL_FORMAT_HINT = 'Enter a valid email address';
const EMAIL_VALID_HINT = '\u2713 Valid email address';

/** Account owners and admins can create users, so only they are offered "+ Add New Manager". */
const USER_TYPES_THAT_CAN_ADD_MANAGERS: string[] = [
  UserType.OWNER,
  UserType.PRIMARY_OWNER,
  UserType.ADMIN,
  UserType.SUPER_ADMIN,
];

/** Format a manager for the dropdown display: "First Last – email". */
const formatManagerDisplay = (mgr: LocationManagerData): string => {
  if (mgr.firstName && mgr.lastName) {
    return `${mgr.firstName} ${mgr.lastName} \u2013 ${mgr.username}`;
  }
  return mgr.username;
};

/**
 * True when this manager consented to texts but their number is known undeliverable.
 *
 * Opting out wins over a stale verification failure — the same rule `getSmsState` applies to the
 * Users table badge — so someone who isn't enrolled in texts is never flagged.
 */
const hasFailedSmsVerification = (mgr: LocationManagerData): boolean =>
  !!mgr.isOptedIntoText && mgr.smsVerified === false;

/** Tooltip for each dropdown option. */
const formatManagerTooltip = (mgr: LocationManagerData): string => {
  if (!mgr.phoneNumber) {
    return 'This user does not have a phone number on file and cannot receive text notifications.';
  }
  if (hasFailedSmsVerification(mgr)) {
    return `Phone: ${formatPhoneNumber(mgr.phoneNumber)} \u2014 this number could not be verified `
      + 'for text messages. Update it in Manage Users to enable notifications.';
  }
  return `Phone: ${formatPhoneNumber(mgr.phoneNumber)}`;
};

/** Green for a valid value, otherwise MUI's default (red while the field is in error). */
const validHintProps = (valid: boolean) => (valid ? { sx: { color: 'success.main' } } : undefined);

const NEW_MANAGER_LABEL = '+ Add New Manager';

const TEXT_OPT_OUT_MESSAGE =
  "If unchecked, the location manager will not receive booking requests via text. " +
  "Please note: every booking has a 30-minute response window before it\u2019s auto-declined. " +
  "Fast responses lead to more bookings \u2014 SMS alerts help make that easier.";

const TEXT_OPT_IN_MESSAGE =
  "Checking this box acknowledges that the location manager will be signed up to receive " +
  "text message notifications for booking requests. Standard messaging rates may apply.";

/**
 * Step 3 (Location Manager) of the Create/Edit Location wizard.
 *
 * Lets the supplier select a location manager from their account's user list
 * and toggle the SMS text opt-in preference.
 */
const LocationManagerStep = (props: LocationManagerStepProps) => {
  const { data, updateData } = props;
  const { user: account } = useContext(AppContext);
  const accountId = account?.id;
  const newManagerRoleName =
    UserRoleToTypeName[getNewLocationManagerRole(account?.subscriptionType) as keyof typeof UserRoleToTypeName];
  const canAddNewManager = USER_TYPES_THAT_CAN_ADD_MANAGERS.includes(account?.userType ?? '');

  // Cached across step navigation so returning to this step renders instantly
  // without a re-fetch flash.
  const { managers, loading: loadingManagers, error: managerLoadError } =
    useLocationManagers(accountId);

  const handleManagerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const username = e.target.value;
      if (username === ADD_NEW_MANAGER_VALUE) {
        updateData({ locationManager: null, newLocationManager: createEmptyNewLocationManager() });
        return;
      }
      // Every other choice leaves "+ Add New Manager", discarding whatever was typed into it.
      if (!username) {
        updateData({ locationManager: null, newLocationManager: null });
        return;
      }
      const selected = managers.find((m) => m.username === username);
      if (selected) {
        updateData({
          newLocationManager: null,
          locationManager: {
            username: selected.username,
            firstName: selected.firstName || '',
            lastName: selected.lastName || '',
            phoneNumber: selected.phoneNumber || '',
            isOptedIntoText: selected.isOptedIntoText,
            smsVerified: selected.smsVerified ?? null,
          },
        });
      }
    },
    [managers, updateData]
  );

  const handleNewManagerChange = useCallback(
    (partial: Partial<NewLocationManagerData>) => {
      // Derive from the latest state so two edits in one tick can't overwrite each other.
      updateData((prev) =>
        prev.newLocationManager ? { newLocationManager: { ...prev.newLocationManager, ...partial } } : {}
      );
    },
    [updateData]
  );

  const handleTextOptIn = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (data.newLocationManager) {
        handleNewManagerChange({ isOptedIntoText: e.target.checked });
        return;
      }
      if (!data.locationManager) return;
      updateData({
        locationManager: {
          ...data.locationManager,
          isOptedIntoText: e.target.checked,
        },
      });
    },
    [data.locationManager, data.newLocationManager, handleNewManagerChange, updateData]
  );

  const isAddingNewManager = !!data.newLocationManager;
  const newManagerErrors = useMemo(
    () => (data.newLocationManager ? getNewLocationManagerErrors(data.newLocationManager) : {}),
    [data.newLocationManager]
  );
  const selectedUsername = isAddingNewManager ? ADD_NEW_MANAGER_VALUE : data.locationManager?.username || '';
  const isOptedIn = !!(data.newLocationManager ?? data.locationManager)?.isOptedIntoText;
  // Flagged only while they are opted in: the phone field here is read-only, so the fix lives in
  // Manage Users, and there is nothing to warn about for someone not enrolled in texts.
  const smsVerificationFailed = !!data.locationManager && hasFailedSmsVerification(data.locationManager);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Location Manager
      </Typography>

      {managerLoadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {managerLoadError}
        </Alert>
      )}

      <TextField
        select
        label="Select A Location Manager"
        fullWidth
        value={selectedUsername}
        onChange={handleManagerChange}
        disabled={loadingManagers && managers.length === 0}
        helperText={
          loadingManagers
            ? 'Loading available managers…'
            : 'The location manager receives booking notifications and manages day-to-day operations for this location.'
        }
        sx={{ mb: 3 }}
        InputProps={
          loadingManagers
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ),
              }
            : undefined
        }
        SelectProps={{
          renderValue: (value) => {
            if (!value) return <em>Choose</em>;
            if (value === ADD_NEW_MANAGER_VALUE) return NEW_MANAGER_LABEL;
            // Try to find the manager in the loaded list first
            const mgr = managers.find((m) => m.username === value);
            if (mgr) return formatManagerDisplay(mgr);
            // Fallback to the saved data (managers list may still be loading)
            if (data.locationManager && data.locationManager.username === value) {
              return formatManagerDisplay(data.locationManager);
            }
            return value as string;
          },
        }}
      >
        <MenuItem value="">
          <em>Choose</em>
        </MenuItem>
        {managers.map((mgr) => {
          // Managers without a phone number can't receive the SMS booking
          // notifications this role depends on, so they're disabled (greyed out)
          // with an inline annotation explaining why.
          const noPhone = !mgr.phoneNumber;
          // A number that failed Twilio Lookup is flagged but still selectable — being
          // unreachable by SMS doesn't disqualify someone from managing a location, it just
          // means notifications won't reach them until the number is fixed in Manage Users.
          const smsUnverified = !noPhone && hasFailedSmsVerification(mgr);
          return (
            <MenuItem
              key={mgr.username}
              value={mgr.username}
              disabled={noPhone}
              title={formatManagerTooltip(mgr)}
            >
              {formatManagerDisplay(mgr)}
              {noPhone && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'warning.dark', fontWeight: 600, opacity: 1 }}
                >
                  (no phone number)
                </Typography>
              )}
              {smsUnverified && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'error.main', fontWeight: 600, opacity: 1 }}
                >
                  (unverified number)
                </Typography>
              )}
            </MenuItem>
          );
        })}
        {canAddNewManager && <Divider key="add-new-manager-divider" component="li" />}
        {canAddNewManager && (
          <MenuItem key={ADD_NEW_MANAGER_VALUE} value={ADD_NEW_MANAGER_VALUE} sx={{ fontWeight: 600 }}>
            {NEW_MANAGER_LABEL}
          </MenuItem>
        )}
      </TextField>

      {/* Editable form for "+ Add New Manager". Same two-column layout as the read-only panel. */}
      {data.newLocationManager && (
        <Box
          component="fieldset"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
            p: 0,
            m: 0,
            border: 0,
            minWidth: 0,
          }}
        >
          <Box component="legend" sx={visuallyHidden}>New location manager details</Box>
          <TextField
            label="First Name"
            value={data.newLocationManager.firstName}
            onChange={(e) => handleNewManagerChange({ firstName: e.target.value })}
            required
            fullWidth
            autoComplete="off"
            error={!!data.newLocationManager.firstName.trim() && !!newManagerErrors.firstName}
            helperText={data.newLocationManager.firstName.trim() ? newManagerErrors.firstName || ' ' : ' '}
          />
          <TextField
            label="Last Name"
            value={data.newLocationManager.lastName}
            onChange={(e) => handleNewManagerChange({ lastName: e.target.value })}
            required
            fullWidth
            autoComplete="off"
            error={!!data.newLocationManager.lastName.trim() && !!newManagerErrors.lastName}
            helperText={data.newLocationManager.lastName.trim() ? newManagerErrors.lastName || ' ' : ' '}
          />
          <PhoneInput
            country="US"
            value={data.newLocationManager.phoneNumber}
            onChange={(value?: string) => handleNewManagerChange({ phoneNumber: value || '' })}
            inputComponent={PhoneNumberTextField}
            label="Phone Number"
            placeholder="e.g. +12025550100"
            required
            fullWidth
            error={!!data.newLocationManager.phoneNumber && !!newManagerErrors.phoneNumber}
            helperText={
              data.newLocationManager.phoneNumber
                ? (newManagerErrors.phoneNumber ? PHONE_FORMAT_HINT : PHONE_VALID_HINT)
                : ' '
            }
            FormHelperTextProps={validHintProps(!!data.newLocationManager.phoneNumber && !newManagerErrors.phoneNumber)}
          />
          <TextField
            label="Email"
            type="email"
            value={data.newLocationManager.email}
            onChange={(e) => handleNewManagerChange({ email: e.target.value })}
            required
            fullWidth
            autoComplete="off"
            error={!!data.newLocationManager.email && !!newManagerErrors.email}
            helperText={
              data.newLocationManager.email
                ? (newManagerErrors.email ? EMAIL_FORMAT_HINT : EMAIL_VALID_HINT)
                : ' '
            }
            FormHelperTextProps={validHintProps(!!data.newLocationManager.email && !newManagerErrors.email)}
          />
        </Box>
      )}

      {data.newLocationManager && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This person will be added to your account with the {newManagerRoleName} role and emailed an
          invitation to create a password.
        </Alert>
      )}

      {/* Read-only details panel for the selected manager */}
      {data.locationManager && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            label="First Name"
            value={data.locationManager.firstName}
            fullWidth
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1 }}
          />
          <TextField
            label="Last Name"
            value={data.locationManager.lastName}
            fullWidth
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1 }}
          />
          <TextField
            label="Phone"
            value={data.locationManager.phoneNumber ? formatPhoneNumber(data.locationManager.phoneNumber) : 'N/A'}
            fullWidth
            error={smsVerificationFailed}
            helperText={
              smsVerificationFailed
                ? 'This number could not be verified for text messages. Update it in Manage Users to enable notifications.'
                : undefined
            }
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1 }}
          />
          <TextField
            label="Email"
            value={data.locationManager.username}
            fullWidth
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1 }}
          />
        </Box>
      )}

      {(data.locationManager || data.newLocationManager) && (
        <>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={isOptedIn}
                onChange={handleTextOptIn}
              />
            }
            label="Text Opt-In"
          />

          <Alert
            severity={isOptedIn ? 'success' : 'warning'}
            icon={false}
            sx={{
              mt: 1,
              '& .MuiAlert-message': { color: 'text.primary' },
              // The theme's `success.light` is a near-transparent green, so the
              // default standard-variant background reads as colorless. Force a
              // clearly green tint when opted in so the panel visibly switches.
              ...(isOptedIn && { bgcolor: 'rgba(76, 175, 80, 0.14)' }),
            }}
          >
            {isOptedIn ? TEXT_OPT_IN_MESSAGE : TEXT_OPT_OUT_MESSAGE}
          </Alert>
        </>
      )}
    </Box>
  );
};

export default LocationManagerStep;
